import { distinctUntilChanged, type Observable } from 'rxjs'
import type Member from 'iam/models/Member'
import type UserData from 'iam/models/UserData'
import type { Id } from 'types/definitions'
import { filterNonNil } from 'utils/rx-util'
import { createSessionRecordingService } from './SessionRecordingService'
import { switchMap, take } from 'rxjs/operators'

export * from './SessionRecordingService'
export * from './PostHogSessionRecordingService'
export * from './NoOpSessionRecordingService'

export type SessionRecordingFeature = ReturnType<typeof SessionRecordingFeature>

/**
 * Initializes session recording by wiring up authentication lifecycle events.
 * Identifies the user on login and resets the session on logout.
 */
export function SessionRecordingFeature(
  currentMember$: Observable<Member>,
  logout$: Observable<void>,
  fetchUserData: (publicId: Id) => Promise<UserData>,
) {
  const sessionRecordingService = createSessionRecordingService()

  // Identify user once when they log in
  currentMember$
    .pipe(
      filterNonNil(),
      distinctUntilChanged(),
      take(1),
      switchMap((member) =>
        fetchUserData(member.user.publicId).then((userData) => ({
          member,
          userData,
        })),
      ),
    )
    .subscribe(({ member, userData }) => {
      sessionRecordingService.identify(member, userData)
    })

  // Reset session data when user logs out
  logout$.pipe(take(1)).subscribe(() => {
    sessionRecordingService.reset()
  })

  return {
    sessionRecordingService,
  }
}
